import React, {
  createContext,
  useContext,
  ChangeEvent,
  useState,
  KeyboardEvent
} from 'react';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import RevealDropdown from 'components/base/RevealDropdown';
import { Dropdown, Modal, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEdit, faPlus } from '@fortawesome/free-solid-svg-icons';
import SearchBox from 'components/common/SearchBox';
import {
  createBXPTreatment,
  updateBXPTreatment,
  deleteBXPTreatment,
  BXPTreatment
} from 'services/hvaservicecampaign.service';

interface TreatmentsTableProps {
  treatments: BXPTreatment[];
  onTreatmentsChange: () => Promise<void>;
}

const TreatmentsChangeContext = createContext<
  (() => Promise<void>) | undefined
>(undefined);

const useTreatmentsChange = () => {
  const context = useContext(TreatmentsChangeContext);
  if (context === undefined) {
    throw new Error(
      'useTreatmentsChange must be used within a TreatmentsChangeProvider'
    );
  }
  return context;
};

type TreatmentActionCellProps = {
  treatment: BXPTreatment;
};

const TreatmentActionCell = ({ treatment }: TreatmentActionCellProps) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  // eslint-disable-next-line
  const [selectedTreatment, setSelectedTreatment] = useState<BXPTreatment | null>(null);
  const onTreatmentsChange = useTreatmentsChange();

  const handleCloseDeleteModal = () => setShowDeleteModal(false);
  const handleShowDeleteModal = () => setShowDeleteModal(true);

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedTreatment(null);
  };

  const handleShowEditModal = () => {
    setSelectedTreatment(treatment);
    setShowEditModal(true);
  };

  const handleConfirmRemove = async () => {
    try {
      await deleteBXPTreatment(treatment.id);
      console.log(`Treatment with ID: ${treatment.id} deleted successfully`);
      await onTreatmentsChange();
    } catch (error) {
      console.error(`Error deleting Treatment with ID: ${treatment.id}`, error);
    } finally {
      handleCloseDeleteModal();
    }
  };

  const handleEditSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedTreatment) {
      try {
        await updateBXPTreatment(selectedTreatment.id, selectedTreatment);
        await onTreatmentsChange();
        handleCloseEditModal();
      } catch (error) {
        alert('Error updating treatment: ' + error);
      }
    }
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    let newValue: string | boolean | number = value;

    if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;
    } else if (type === 'number') {
      newValue = parseInt(value, 10);
    }

    setSelectedTreatment(prev => (prev ? { ...prev, [name]: newValue } : null));
  };

  return (
    <>
      <RevealDropdown btnClassName="p-2 pt-1 pb-1 fs-11">
        <Dropdown.Item onClick={handleShowEditModal}>
          <FontAwesomeIcon icon={faEdit} className="fs-8 p-1 pt-0 pb-0" />
          Edit
        </Dropdown.Item>
        <Dropdown.Item onClick={handleShowDeleteModal} className="text-danger">
          <FontAwesomeIcon icon={faTrash} className="fs-8 p-1 pt-0 pb-0" />
          Remove
        </Dropdown.Item>
      </RevealDropdown>

      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this Treatment?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmRemove}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEditModal}
        onHide={handleCloseEditModal}
        size="lg"
        centered
      >
        <Form onSubmit={handleEditSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Update Treatment</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6">
            <Form.Group className="mb-3">
              <Form.Label>Short Name</Form.Label>
              <Form.Control
                type="text"
                name="shortname"
                value={selectedTreatment?.shortname || ''}
                onChange={handleInputChange}
                required
                maxLength={32}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Max Attempts per Record</Form.Label>
              <Form.Control
                type="number"
                name="max_attempts_per_record"
                value={selectedTreatment?.max_attempts_per_record || 0}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Daily Attempts per Record</Form.Label>
              <Form.Control
                type="number"
                name="daily_attempts_per_record"
                value={selectedTreatment?.daily_attempts_per_record || 0}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Next Attempt After (mins)</Form.Label>
              <Form.Control
                type="number"
                name="next_attempt_after_mins"
                value={selectedTreatment?.next_attempt_after_mins || 0}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Next Chain After (mins)</Form.Label>
              <Form.Control
                type="number"
                name="next_chain_after_mins"
                value={selectedTreatment?.next_chain_after_mins || 0}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>List Iteration Schema</Form.Label>
              <Form.Select
                name="list_iteration_schema"
                value={selectedTreatment?.list_iteration_schema || ''}
                onChange={handleInputChange}
              >
                <option value=""></option>
                <option value="redial">redial</option>
                <option value="next-in-chain">next-in-chain</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Next Recall Record After (days)</Form.Label>
              <Form.Control
                type="number"
                name="next_recall_record_after_days"
                value={selectedTreatment?.next_recall_record_after_days || 0}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Max Attempts per Contact</Form.Label>
              <Form.Control
                type="number"
                name="maxattempts_per_contact"
                value={selectedTreatment?.maxattempts_per_contact || 0}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Daily Attempts per Contact</Form.Label>
              <Form.Control
                type="number"
                name="daily_attempts_per_contact"
                value={selectedTreatment?.daily_attempts_per_contact || 0}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Next Recall Contact After (days)</Form.Label>
              <Form.Control
                type="number"
                name="next_recall_contact_after_days"
                value={selectedTreatment?.next_recall_contact_after_days || 0}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <div className="form-check form-switch pb-1 pt-1">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    name="active"
                    checked={selectedTreatment?.active || false}
                    onChange={handleInputChange}
                    className="form-check-input"
                  />
                  Active
                </label>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEditModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Update Treatment
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export const treatmentsTableColumns: ColumnDef<BXPTreatment>[] = [
  {
    accessorKey: 'id',
    header: 'ID',
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'fw-bold px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'shortname',
    header: 'Short Name',
    meta: {
      headerProps: { style: { width: '15%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'action',
    enableSorting: false,
    header: '',
    cell: ({ row: { original } }) => {
      return <TreatmentActionCell treatment={original} />;
    },
    meta: {
      cellProps: { className: 'text-body-tertiary' }
    }
  },
  {
    accessorKey: 'max_attempts_per_record',
    header: 'Max Attempts per Record',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'daily_attempts_per_record',
    header: 'Daily Attempts per Record',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'next_attempt_after_mins',
    header: 'Next Attempt After (mins)',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'next_chain_after_mins',
    header: 'Next Chain After (mins)',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'list_iteration_schema',
    header: 'List Iteration Schema',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'next_recall_record_after_days',
    header: 'Next Recall Record After (days)',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'maxattempts_per_contact',
    header: 'Max Attempts per Contact',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'daily_attempts_per_contact',
    header: 'Daily Attempts per Contact',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'next_recall_contact_after_days',
    header: 'Next Recall Contact After (days)',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'active',
    header: 'Active',
    cell: ({ row }) => (row.original.active ? 'Yes' : 'No'),
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  }
];

export const BXPTreatmentsTable: React.FC<TreatmentsTableProps> = ({
  treatments,
  onTreatmentsChange
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTreatment, setNewTreatment] = useState<BXPTreatment>({
    id: 0,
    shortname: '',
    max_attempts_per_record: 0,
    daily_attempts_per_record: 0,
    next_attempt_after_mins: 0,
    next_chain_after_mins: 0,
    list_iteration_schema: '',
    next_recall_record_after_days: 0,
    maxattempts_per_contact: 0,
    daily_attempts_per_contact: 0,
    next_recall_contact_after_days: 0,
    active: true
  });
  const table = useAdvanceTable({
    data: treatments,
    columns: treatmentsTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  const handleSearchKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleShowAddModal = () => setShowAddModal(true);
  const handleCloseAddModal = () => setShowAddModal(false);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    let newValue: string | boolean | number = value;

    if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;
    } else if (type === 'number') {
      newValue = parseInt(value, 10);
    }

    setNewTreatment(prev => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await createBXPTreatment(newTreatment);
      handleCloseAddModal();
      onTreatmentsChange();
    } catch (error) {
      console.error('Error creating treatment:', error);
    }
  };

  return (
    <TreatmentsChangeContext.Provider value={onTreatmentsChange}>
      <AdvanceTableProvider {...table}>
        <div className="mb-4">
          <div className="d-flex flex-wrap gap-3">
            <SearchBox
              placeholder="Search Treatments"
              onChange={handleSearchInputChange}
              onKeyDown={handleSearchKeyPress}
            />
            <div className="ms-auto">
              <Button
                variant="primary"
                className="p-2"
                onClick={handleShowAddModal}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2 fs-8" />
                Add Treatment
              </Button>
            </div>
          </div>
        </div>
        <div>
          <AdvanceTable
            tableProps={{ className: 'phoenix-table fs-9' }}
            rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
          />
          <AdvanceTableFooter pagination />
        </div>
      </AdvanceTableProvider>

      <Modal
        show={showAddModal}
        onHide={handleCloseAddModal}
        size="lg"
        centered
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Add New Treatment</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6">
            <Form.Group className="mb-3">
              <Form.Label>Short Name</Form.Label>
              <Form.Control
                type="text"
                name="shortname"
                value={newTreatment.shortname}
                onChange={handleInputChange}
                required
                maxLength={32}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Max Attempts per Record</Form.Label>
              <Form.Control
                type="number"
                name="max_attempts_per_record"
                value={newTreatment.max_attempts_per_record}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Daily Attempts per Record</Form.Label>
              <Form.Control
                type="number"
                name="daily_attempts_per_record"
                value={newTreatment.daily_attempts_per_record}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Next Attempt After (mins)</Form.Label>
              <Form.Control
                type="number"
                name="next_attempt_after_mins"
                value={newTreatment.next_attempt_after_mins}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Next Chain After (mins)</Form.Label>
              <Form.Control
                type="number"
                name="next_chain_after_mins"
                value={newTreatment.next_chain_after_mins}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>List Iteration Schema</Form.Label>
              <Form.Select
                name="list_iteration_schema"
                value={newTreatment.list_iteration_schema}
                onChange={handleInputChange}
              >
                <option value=""></option>
                <option value="redial">redial</option>
                <option value="next-in-chain">next-in-chain</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Next Recall Record After (days)</Form.Label>
              <Form.Control
                type="number"
                name="next_recall_record_after_days"
                value={newTreatment.next_recall_record_after_days}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Max Attempts per Contact</Form.Label>
              <Form.Control
                type="number"
                name="maxattempts_per_contact"
                value={newTreatment.maxattempts_per_contact}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Daily Attempts per Contact</Form.Label>
              <Form.Control
                type="number"
                name="daily_attempts_per_contact"
                value={newTreatment.daily_attempts_per_contact}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Next Recall Contact After (days)</Form.Label>
              <Form.Control
                type="number"
                name="next_recall_contact_after_days"
                value={newTreatment.next_recall_contact_after_days}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <div className="form-check form-switch pb-1 pt-1">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    name="active"
                    checked={newTreatment.active}
                    onChange={handleInputChange}
                    className="form-check-input"
                  />
                  Active
                </label>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseAddModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Add Treatment
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </TreatmentsChangeContext.Provider>
  );
};
